import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Palette, Type, Image, Layers, Check, Upload, Sparkles, Eye,
  Plus, Star, MoreVertical, Pencil, Copy, Trash2, Search, Grid, List,
  X, Frame
} from 'lucide-react';
import brandKitService from '../../../services/brandKitService';
import { useBrandKit } from '../../../context/BrandKitContext';
import uploadService from '../../../services/uploadService';

// ─── Inline UI Primitives ────────────────────────────────────────

const Switch = ({ checked, onCheckedChange }) => (
  <button
    type="button"
    onClick={() => onCheckedChange(!checked)}
    className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-[#6a1fbf]' : 'bg-white/20'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

const Slider = ({ value, onValueChange, min = 0, max = 100, step = 1 }) => {
  const pct = ((value[0] - min) / (max - min)) * 100;
  return (
    <div className="relative h-6 flex items-center">
      <div className="w-full h-2 bg-white/20 rounded-full">
        <div className="h-full bg-[#6a1fbf] rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <input type="range" min={min} max={max} step={step} value={value[0]}
        onChange={(e) => onValueChange([parseFloat(e.target.value)])}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
      <div className="absolute w-4 h-4 bg-white rounded-full shadow-md pointer-events-none"
        style={{ left: `calc(${pct}% - 8px)` }} />
    </div>
  );
};

const SelectDropdown = ({ value, onChange, options, placeholder, className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const selected = options.find(o => o.value === value);
  return (
    <div className={`relative ${className}`} ref={ref}>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:border-white/20 transition-colors">
        <span className={selected ? '' : 'text-gray-500'}>{selected ? selected.label : placeholder}</span>
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto">
          {options.map(o => (
            <button key={o.value} type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${o.value === value ? 'text-[#6a1fbf]' : 'text-gray-300'}`}
              style={o.style || {}}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ColorInput = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
    <div className="flex gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-12 h-10 rounded-lg cursor-pointer bg-transparent border border-white/10" />
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono text-sm outline-none focus:border-[#6a1fbf]" />
    </div>
  </div>
);

// ─── Constants ───────────────────────────────────────────────────

const FONTS = ['Inter', 'Roboto', 'Open Sans', 'Poppins', 'Montserrat', 'Lato', 'Playfair Display'];
const FRAME_STYLES = ['none', 'rounded', 'sharp', 'gradient'];
const LOWER_THIRD_TEMPLATES = ['minimal', 'modern', 'classic', 'gradient'];
const WATERMARK_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

const DEFAULT_BRAND_KIT = {
  name: '',
  description: '',
  colors: { primary: '#6a1fbf', accent: '#00c853', background: '#001233', text: '#ffffff', secondary_text: '#9ca3af' },
  fonts: { heading: 'Inter', body: 'Inter', caption: 'Inter' },
  logos: { main_logo_url: '', square_logo_url: '', watermark_url: '' },
  frame_settings: { style: 'rounded', border_width: 2, border_color: '#6a1fbf', shadow: true },
  lower_third: { template: 'modern', background_color: '#001233', text_color: '#ffffff', accent_color: '#6a1fbf', show_logo: true },
  overlay_defaults: { watermark_position: 'bottom-right', watermark_opacity: 0.7 },
};

const BRAND_TEMPLATES = [
  { id: 'corporate-blue', name: 'Corporate Blue', category: 'corporate', description: 'Professional business identity with trustworthy blue tones', colors: { primary: '#0066cc', accent: '#00a8e8', background: '#001233', text: '#ffffff', secondary_text: '#9ca3af' }, fonts: { heading: 'Roboto', body: 'Roboto', caption: 'Roboto' }, preview: 'linear-gradient(135deg, #0066cc, #00a8e8)' },
  { id: 'creative-vibrant', name: 'Creative Vibrant', category: 'creative', description: 'Bold and energetic design for creative industries', colors: { primary: '#ff6b35', accent: '#f7931e', background: '#1a1a2e', text: '#ffffff', secondary_text: '#a8a8b8' }, fonts: { heading: 'Poppins', body: 'Open Sans', caption: 'Open Sans' }, preview: 'linear-gradient(135deg, #ff6b35, #f7931e)' },
  { id: 'minimal-mono', name: 'Minimal Monochrome', category: 'minimal', description: 'Clean and elegant black and white aesthetic', colors: { primary: '#2d2d2d', accent: '#666666', background: '#0a0a0a', text: '#ffffff', secondary_text: '#999999' }, fonts: { heading: 'Inter', body: 'Inter', caption: 'Inter' }, preview: 'linear-gradient(135deg, #2d2d2d, #666666)' },
  { id: 'tech-purple', name: 'Tech Purple', category: 'modern', description: 'Modern tech look with purple and teal accents', colors: { primary: '#6a1fbf', accent: '#00c853', background: '#001233', text: '#ffffff', secondary_text: '#9ca3af' }, fonts: { heading: 'Montserrat', body: 'Lato', caption: 'Lato' }, preview: 'linear-gradient(135deg, #6a1fbf, #00c853)' },
  { id: 'elegant-gold', name: 'Elegant Gold', category: 'elegant', description: 'Luxurious design with gold and deep navy', colors: { primary: '#c9a55c', accent: '#8b7355', background: '#0a1628', text: '#ffffff', secondary_text: '#b8b8b8' }, fonts: { heading: 'Playfair Display', body: 'Lato', caption: 'Lato' }, preview: 'linear-gradient(135deg, #c9a55c, #8b7355)' },
  { id: 'bold-red', name: 'Bold Red', category: 'bold', description: 'Powerful and attention-grabbing red theme', colors: { primary: '#dc143c', accent: '#ff4757', background: '#1a0a0f', text: '#ffffff', secondary_text: '#cccccc' }, fonts: { heading: 'Montserrat', body: 'Open Sans', caption: 'Open Sans' }, preview: 'linear-gradient(135deg, #dc143c, #ff4757)' },
];

const TEMPLATE_CATEGORIES = [
  { id: 'all', label: 'All Templates' },
  { id: 'corporate', label: 'Corporate' },
  { id: 'creative', label: 'Creative' },
  { id: 'minimal', label: 'Minimal' },
  { id: 'bold', label: 'Bold' },
  { id: 'elegant', label: 'Elegant' },
  { id: 'modern', label: 'Modern' },
];

// ─── Brand Kit Editor Modal ─────────────────────────────────────

function BrandKitEditorModal({ isOpen, onClose, onSave, brandKit }) {
  const [formData, setFormData] = useState(DEFAULT_BRAND_KIT);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('colors');

  useEffect(() => {
    if (brandKit) {
      setFormData({
        ...DEFAULT_BRAND_KIT,
        ...brandKit,
        colors: { ...DEFAULT_BRAND_KIT.colors, ...brandKit.colors },
        fonts: { ...DEFAULT_BRAND_KIT.fonts, ...brandKit.fonts },
        logos: { ...DEFAULT_BRAND_KIT.logos, ...brandKit.logos },
        frame_settings: { ...DEFAULT_BRAND_KIT.frame_settings, ...brandKit.frame_settings },
        lower_third: { ...DEFAULT_BRAND_KIT.lower_third, ...brandKit.lower_third },
        overlay_defaults: { ...DEFAULT_BRAND_KIT.overlay_defaults, ...brandKit.overlay_defaults },
      });
    } else {
      setFormData({ ...DEFAULT_BRAND_KIT, name: 'New Brand Kit' });
    }
    setActiveTab('colors');
  }, [brandKit, isOpen]);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(formData); } finally { setSaving(false); }
  };

  const updateNested = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleLogoUpload = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const result = await uploadService.uploadFile(file);
      if (result?.url) updateNested(`logos.${key}`, result.url);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'colors', label: 'Colors', icon: Palette },
    { id: 'fonts', label: 'Fonts', icon: Type },
    { id: 'logos', label: 'Logos', icon: Image },
    { id: 'frames', label: 'Frames', icon: Frame },
    { id: 'overlays', label: 'Overlays', icon: Layers },
  ];

  const fontOptions = FONTS.map(f => ({ value: f, label: f, style: { fontFamily: f } }));

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#0a0a1a] rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-white/10 flex flex-col"
        >
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg font-semibold text-white">
              {brandKit ? 'Edit Brand Kit' : 'Create Brand Kit'}
            </h2>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10 transition-colors">
                <Eye className="w-4 h-4" />
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 min-h-0">
            {/* Editor */}
            <div className={`flex-1 overflow-y-auto ${showPreview ? 'max-w-2xl' : ''}`}>
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="text-gray-400 text-sm block mb-1">Brand Kit Name</label>
                    <input value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm outline-none focus:border-[#6a1fbf]"
                      placeholder="My Brand Kit" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm block mb-1">Description (optional)</label>
                    <textarea value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm outline-none focus:border-[#6a1fbf] h-16 resize-none"
                      placeholder="Describe this brand kit..." />
                  </div>
                </div>

                {/* Tabs */}
                <div>
                  <div className="flex bg-white/5 border border-white/10 rounded-lg p-1 gap-1">
                    {tabs.map(t => (
                      <button key={t.id} onClick={() => setActiveTab(t.id)}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === t.id ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}>
                        <t.icon className="w-4 h-4" />{t.label}
                      </button>
                    ))}
                  </div>

                  {/* Colors Tab */}
                  {activeTab === 'colors' && (
                    <div className="mt-6 bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
                      <h3 className="text-lg font-semibold text-white">Brand Colors</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ColorInput label="Primary Color" value={formData.colors.primary} onChange={v => updateNested('colors.primary', v)} />
                        <ColorInput label="Accent Color" value={formData.colors.accent} onChange={v => updateNested('colors.accent', v)} />
                        <ColorInput label="Background" value={formData.colors.background} onChange={v => updateNested('colors.background', v)} />
                        <ColorInput label="Text Color" value={formData.colors.text} onChange={v => updateNested('colors.text', v)} />
                        <ColorInput label="Secondary Text" value={formData.colors.secondary_text} onChange={v => updateNested('colors.secondary_text', v)} />
                      </div>
                    </div>
                  )}

                  {/* Fonts Tab */}
                  {activeTab === 'fonts' && (
                    <div className="mt-6 bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
                      <h3 className="text-lg font-semibold text-white">Typography</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {['heading', 'body', 'caption'].map(type => (
                          <div key={type}>
                            <label className="block text-sm font-medium text-gray-300 mb-2 capitalize">{type} Font</label>
                            <SelectDropdown
                              value={formData.fonts[type]}
                              onChange={v => updateNested(`fonts.${type}`, v)}
                              options={fontOptions}
                              placeholder="Select font" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Logos Tab */}
                  {activeTab === 'logos' && (
                    <div className="mt-6 bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
                      <h3 className="text-lg font-semibold text-white">Logo Assets</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                          { key: 'main_logo_url', label: 'Main Logo', desc: 'Used in headers & overlays' },
                          { key: 'square_logo_url', label: 'Square Logo', desc: 'For social & thumbnails' },
                          { key: 'watermark_url', label: 'Watermark', desc: 'Video overlay watermark' },
                        ].map(({ key, label, desc }) => (
                          <div key={key} className="space-y-2">
                            <label className="block text-sm font-medium text-gray-300">{label}</label>
                            <p className="text-xs text-gray-500">{desc}</p>
                            <div className="aspect-video bg-white/5 rounded-xl border border-white/10 border-dashed flex items-center justify-center relative overflow-hidden">
                              {formData.logos[key] ? (
                                <div className="relative w-full h-full group">
                                  <img src={formData.logos[key]} alt={label} className="w-full h-full object-contain p-4" />
                                  <button onClick={() => updateNested(`logos.${key}`, '')}
                                    className="absolute top-1 right-1 p-1 bg-red-500/80 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                    <X className="w-3 h-3 text-white" />
                                  </button>
                                </div>
                              ) : (
                                <label className="cursor-pointer text-center p-4">
                                  <input type="file" accept="image/*" onChange={(e) => handleLogoUpload(e, key)} className="hidden" />
                                  <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                                  <span className="text-sm text-gray-500">{isUploading ? 'Uploading...' : 'Upload'}</span>
                                </label>
                              )}
                            </div>
                            <input value={formData.logos[key]}
                              onChange={(e) => updateNested(`logos.${key}`, e.target.value)}
                              placeholder="Or enter logo URL"
                              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm outline-none focus:border-[#6a1fbf]" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Frames Tab */}
                  {activeTab === 'frames' && (
                    <div className="mt-6 bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
                      <h3 className="text-lg font-semibold text-white">Video Frame Settings</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Frame Style</label>
                          <SelectDropdown
                            value={formData.frame_settings.style}
                            onChange={v => updateNested('frame_settings.style', v)}
                            options={FRAME_STYLES.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                            placeholder="Select style" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Border Width: {formData.frame_settings.border_width}px</label>
                          <Slider value={[formData.frame_settings.border_width]} onValueChange={([v]) => updateNested('frame_settings.border_width', v)} min={0} max={10} step={1} />
                        </div>
                        <ColorInput label="Border Color" value={formData.frame_settings.border_color} onChange={v => updateNested('frame_settings.border_color', v)} />
                        <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                          <span className="text-sm text-gray-300">Enable Shadow</span>
                          <Switch checked={formData.frame_settings.shadow} onCheckedChange={v => updateNested('frame_settings.shadow', v)} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Overlays Tab */}
                  {activeTab === 'overlays' && (
                    <div className="mt-6 space-y-6">
                      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
                        <h3 className="text-lg font-semibold text-white">Lower Third Template</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Template Style</label>
                            <SelectDropdown
                              value={formData.lower_third.template}
                              onChange={v => updateNested('lower_third.template', v)}
                              options={LOWER_THIRD_TEMPLATES.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
                              placeholder="Select template" />
                          </div>
                          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                            <span className="text-sm text-gray-300">Show Logo</span>
                            <Switch checked={formData.lower_third.show_logo} onCheckedChange={v => updateNested('lower_third.show_logo', v)} />
                          </div>
                          <ColorInput label="Background" value={formData.lower_third.background_color} onChange={v => updateNested('lower_third.background_color', v)} />
                          <ColorInput label="Text Color" value={formData.lower_third.text_color} onChange={v => updateNested('lower_third.text_color', v)} />
                        </div>
                      </div>

                      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
                        <h3 className="text-lg font-semibold text-white">Watermark Settings</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Position</label>
                            <SelectDropdown
                              value={formData.overlay_defaults.watermark_position}
                              onChange={v => updateNested('overlay_defaults.watermark_position', v)}
                              options={WATERMARK_POSITIONS.map(p => ({ value: p, label: p.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }))}
                              placeholder="Select position" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Opacity: {Math.round(formData.overlay_defaults.watermark_opacity * 100)}%</label>
                            <Slider value={[formData.overlay_defaults.watermark_opacity]} onValueChange={([v]) => updateNested('overlay_defaults.watermark_opacity', v)} min={0} max={1} step={0.05} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Live Preview */}
            {showPreview && (
              <div className="w-80 border-l border-white/10 p-4 bg-black/20 overflow-y-auto flex-shrink-0">
                <h4 className="text-sm font-medium text-gray-400 mb-4">Live Preview</h4>
                <div className="aspect-video rounded-xl overflow-hidden relative"
                  style={{ backgroundColor: formData.colors.background }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: formData.colors.primary,
                        borderWidth: formData.frame_settings.border_width,
                        borderColor: formData.frame_settings.border_color,
                        borderStyle: 'solid',
                        boxShadow: formData.frame_settings.shadow ? '0 10px 30px rgba(0,0,0,0.3)' : 'none'
                      }}>
                      <span style={{ color: formData.colors.text, fontFamily: formData.fonts.heading }} className="text-xl font-bold">JD</span>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-2"
                    style={{ backgroundColor: `${formData.lower_third.background_color}cc` }}>
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-6 rounded" style={{ backgroundColor: formData.colors.accent }} />
                      <div>
                        <p style={{ color: formData.lower_third.text_color, fontFamily: formData.fonts.heading }} className="text-xs font-semibold">John Doe</p>
                        <p style={{ color: formData.colors.secondary_text, fontFamily: formData.fonts.caption }} className="text-[10px]">Product Manager</p>
                      </div>
                    </div>
                  </div>
                  {formData.logos.watermark_url && (
                    <img src={formData.logos.watermark_url} alt="Watermark"
                      className={`absolute w-10 h-10 object-contain ${
                        formData.overlay_defaults.watermark_position === 'top-left' ? 'top-2 left-2' :
                        formData.overlay_defaults.watermark_position === 'top-right' ? 'top-2 right-2' :
                        formData.overlay_defaults.watermark_position === 'bottom-left' ? 'bottom-10 left-2' : 'bottom-10 right-2'
                      }`}
                      style={{ opacity: formData.overlay_defaults.watermark_opacity }} />
                  )}
                </div>
                <div className="mt-4">
                  <h5 className="text-xs text-gray-500 mb-2">Color Palette</h5>
                  <div className="flex gap-2">
                    {Object.entries(formData.colors).map(([key, color]) => (
                      <div key={key} className="w-8 h-8 rounded-lg border border-white/20" style={{ backgroundColor: color }} title={key} />
                    ))}
                  </div>
                </div>
                <div className="mt-4">
                  <h5 className="text-xs text-gray-500 mb-2">Typography</h5>
                  <div className="space-y-1">
                    <p style={{ fontFamily: formData.fonts.heading }} className="text-white font-semibold">Heading Font</p>
                    <p style={{ fontFamily: formData.fonts.body }} className="text-gray-400 text-sm">Body Font</p>
                    <p style={{ fontFamily: formData.fonts.caption }} className="text-gray-500 text-xs">Caption Font</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 flex items-center justify-end gap-2 flex-shrink-0">
            <button onClick={onClose}
              className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !formData.name}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Saving...' : (<><Check className="w-4 h-4" />{brandKit ? 'Save Changes' : 'Create Brand Kit'}</>)}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

// ─── Brand Templates Section ─────────────────────────────────────

function BrandTemplatesSection({ onUseTemplate }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const filteredTemplates = selectedCategory === 'all'
    ? BRAND_TEMPLATES
    : BRAND_TEMPLATES.filter(t => t.category === selectedCategory);

  const handleUseTemplate = (template) => {
    const templateData = {
      name: `${template.name} Brand Kit`,
      description: template.description,
      colors: template.colors,
      fonts: template.fonts,
      logos: { main_logo_url: '', square_logo_url: '', watermark_url: '' },
      frame_settings: { style: 'rounded', border_width: 2, border_color: template.colors.primary, shadow: true },
      lower_third: { template: 'modern', background_color: template.colors.background, text_color: template.colors.text, accent_color: template.colors.primary, show_logo: true },
      overlay_defaults: { watermark_position: 'bottom-right', watermark_opacity: 0.7 },
    };
    onUseTemplate(templateData);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-white mb-2">Brand Kit Templates</h3>
        <p className="text-gray-400">Start with a professional template and customize it to match your brand</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {TEMPLATE_CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => setSelectedCategory(cat.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCategory === cat.id ? 'bg-[#6a1fbf] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
            {cat.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTemplates.map(template => (
          <motion.div key={template.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="group bg-white/5 rounded-xl border border-white/10 overflow-hidden hover:border-[#6a1fbf]/50 transition-all">
            <div className="h-32 relative" style={{ background: template.preview }}>
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                <span className="bg-white/20 text-white backdrop-blur-sm text-xs px-2 py-1 rounded capitalize">{template.category}</span>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <h4 className="font-semibold text-white mb-1">{template.name}</h4>
                <p className="text-sm text-gray-400 line-clamp-2">{template.description}</p>
              </div>
              <div className="flex gap-1">
                {Object.entries(template.colors).slice(0, 5).map(([key, color]) => (
                  <div key={key} className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: color }} title={key} />
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setPreviewTemplate(template)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border border-white/10 bg-white text-gray-900 rounded-lg text-sm hover:bg-white/90 transition-colors">
                  <Eye className="w-4 h-4" />Preview
                </button>
                <button onClick={() => handleUseTemplate(template)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm hover:opacity-90 transition-colors">
                  <Copy className="w-4 h-4" />Use Template
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Preview Modal */}
      {previewTemplate && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
          onClick={() => setPreviewTemplate(null)}>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-[#0a0a1a] rounded-2xl border border-white/10 p-6 max-w-2xl w-full"
            onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-semibold text-white mb-4">{previewTemplate.name}</h3>
            <div className="aspect-video rounded-xl overflow-hidden relative mb-4"
              style={{ backgroundColor: previewTemplate.colors.background }}>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full flex items-center justify-center border-4"
                  style={{ backgroundColor: previewTemplate.colors.primary, borderColor: previewTemplate.colors.accent }}>
                  <span style={{ color: previewTemplate.colors.text, fontFamily: previewTemplate.fonts.heading }} className="text-2xl font-bold">AB</span>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-3"
                style={{ backgroundColor: `${previewTemplate.colors.background}cc` }}>
                <div className="flex items-center gap-2">
                  <div className="w-1 h-8 rounded" style={{ backgroundColor: previewTemplate.colors.accent }} />
                  <div>
                    <p style={{ color: previewTemplate.colors.text, fontFamily: previewTemplate.fonts.heading }} className="text-sm font-semibold">Your Name</p>
                    <p style={{ color: previewTemplate.colors.secondary_text, fontFamily: previewTemplate.fonts.caption }} className="text-xs">Your Title</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Colors</h4>
                <div className="flex gap-2">
                  {Object.entries(previewTemplate.colors).map(([key, color]) => (
                    <div key={key} className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-lg border border-white/20" style={{ backgroundColor: color }} />
                      <span className="text-xs text-gray-500 mt-1 capitalize">{key.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Fonts</h4>
                <div className="space-y-1">
                  <p style={{ fontFamily: previewTemplate.fonts.heading }} className="text-white">Heading: {previewTemplate.fonts.heading}</p>
                  <p style={{ fontFamily: previewTemplate.fonts.body }} className="text-gray-400 text-sm">Body: {previewTemplate.fonts.body}</p>
                  <p style={{ fontFamily: previewTemplate.fonts.caption }} className="text-gray-500 text-xs">Caption: {previewTemplate.fonts.caption}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setPreviewTemplate(null)}
                className="flex-1 px-4 py-2 border border-white/10 bg-white text-gray-900 rounded-lg text-sm hover:bg-white/90 transition-colors">
                Close
              </button>
              <button onClick={() => { handleUseTemplate(previewTemplate); setPreviewTemplate(null); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm hover:opacity-90 transition-colors">
                <Check className="w-4 h-4" />Use This Template
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Dropdown Menu ───────────────────────────────────────────────

function DropdownMenu({ trigger, items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="p-1.5 rounded-lg hover:bg-white/10 transition-opacity">
        {trigger}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 min-w-[180px]">
          {items.map((item, i) =>
            item.separator ? (
              <div key={i} className="my-1 border-t border-white/10" />
            ) : (
              <button key={i} onClick={() => { item.onClick(); setOpen(false); }}
                disabled={item.disabled}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${item.danger ? 'text-red-400' : 'text-gray-300'}`}>
                {item.icon && <item.icon className="w-4 h-4" />}
                {item.label}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main BrandKitPage ──────────────────────────────────────────

export default function BrandKitPage() {
  const { refreshBrandKit } = useBrandKit();
  const [brandKits, setBrandKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showModal, setShowModal] = useState(false);
  const [editingKit, setEditingKit] = useState(null);
  const [activeTab, setActiveTab] = useState('my-kits');

  const loadBrandKits = async () => {
    try {
      const res = await brandKitService.listBrandKits();
      setBrandKits(res.data || []);
    } catch (err) {
      console.error('Failed to load brand kits:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBrandKits(); }, []);

  const filteredKits = brandKits.filter(kit =>
    kit.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    kit.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreate = () => { setEditingKit(null); setShowModal(true); };

  const handleEdit = (kit) => { setEditingKit(kit); setShowModal(true); };

  const handleSaveKit = async (data) => {
    try {
      if (editingKit) {
        await brandKitService.updateBrandKit(editingKit.id, data);
      } else {
        await brandKitService.createBrandKit(data);
      }
      setShowModal(false);
      setEditingKit(null);
      await loadBrandKits();
      if (data.is_default || editingKit?.is_default) {
        await refreshBrandKit();
      }
    } catch (err) {
      console.error('Save failed:', err);
    }
  };

  const handleDuplicate = async (kit) => {
    try {
      const { id, _id, createdAt, updatedAt, ...rest } = kit;
      await brandKitService.createBrandKit({ ...rest, name: `${kit.name} (Copy)`, is_default: false });
      await loadBrandKits();
    } catch (err) {
      console.error('Duplicate failed:', err);
    }
  };

  const handleDelete = async (kit) => {
    if (kit.is_default) {
      alert('Cannot delete default brand kit. Please remove it as default first.');
      return;
    }
    if (!window.confirm(`Delete "${kit.name}"? This cannot be undone.`)) return;
    try {
      await brandKitService.deleteBrandKit(kit.id);
      await loadBrandKits();
      await refreshBrandKit();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleSetDefault = async (kit) => {
    try {
      await brandKitService.setDefault(kit.id);
      await loadBrandKits();
      await refreshBrandKit();
    } catch (err) {
      console.error('Set default failed:', err);
    }
  };

  const handleRemoveDefault = async (kit) => {
    try {
      await brandKitService.removeDefault(kit.id);
      await loadBrandKits();
      await refreshBrandKit();
    } catch (err) {
      console.error('Remove default failed:', err);
    }
  };

  const handleSetGlobalDefault = async (kit) => {
    if (!window.confirm(`Set "${kit.name}" as the global default for all new accounts?`)) return;
    try {
      await brandKitService.setGlobalDefault(kit.id);
      await loadBrandKits();
      await refreshBrandKit();
    } catch (err) {
      console.error('Set global default failed:', err);
    }
  };

  const handleRemoveGlobalDefault = async (kit) => {
    if (!window.confirm(`Remove "${kit.name}" as the global default?`)) return;
    try {
      await brandKitService.removeGlobalDefault(kit.id);
      await loadBrandKits();
      await refreshBrandKit();
    } catch (err) {
      console.error('Remove global default failed:', err);
    }
  };

  const handleUseTemplate = async (templateData) => {
    try {
      await brandKitService.createBrandKit(templateData);
      setActiveTab('my-kits');
      await loadBrandKits();
    } catch (err) {
      console.error('Create from template failed:', err);
    }
  };

  const getFeatureCounts = (kit) => {
    const counts = [];
    const colorCount = Object.values(kit.colors || {}).filter(Boolean).length;
    const fontCount = Object.values(kit.fonts || {}).filter(Boolean).length;
    const logoCount = Object.values(kit.logos || {}).filter(Boolean).length;
    if (colorCount > 0) counts.push({ emoji: '\uD83C\uDFA8', label: 'Colors', count: colorCount });
    if (fontCount > 0) counts.push({ emoji: '\uD83D\uDD24', label: 'Fonts', count: fontCount });
    if (logoCount > 0) counts.push({ emoji: '\uD83D\uDDBC\uFE0F', label: 'Logos', count: logoCount });
    return counts;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6a1fbf]" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Brand Kits</h2>
          <p className="text-gray-400 mt-1">Manage multiple brand identities for your rooms</p>
        </div>
        <button onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg hover:opacity-90 transition-all text-sm font-medium">
          <Plus className="w-4 h-4" />Create Brand Kit
        </button>
      </div>

      {/* Global Default Notice */}
      {(() => {
        const globalKit = brandKits.find(k => k.is_global_default);
        return globalKit ? (
          <div className="bg-gradient-to-r from-[#6a1fbf]/10 to-[#00c853]/10 border border-[#6a1fbf]/20 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-[#6a1fbf]" />
                <div>
                  <p className="text-white font-medium">Global Default: {globalKit.name}</p>
                  <p className="text-sm text-gray-400">This brand kit will be automatically applied to all new accounts</p>
                </div>
              </div>
              <button onClick={() => handleRemoveGlobalDefault(globalKit)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-400 hover:text-red-400 hover:bg-red-500/10 border border-white/10 rounded-lg transition-colors">
                <X className="w-3.5 h-3.5" />
                Remove Global
              </button>
            </div>
          </div>
        ) : null;
      })()}

      {/* Tabs: My Brand Kits / Templates */}
      <div className="flex bg-white/5 border border-white/10 rounded-lg p-1 w-fit gap-1">
        <button onClick={() => setActiveTab('my-kits')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'my-kits' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}>
          My Brand Kits
        </button>
        <button onClick={() => setActiveTab('templates')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'templates' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}>
          <Sparkles className="w-4 h-4" />Templates
        </button>
      </div>

      {activeTab === 'my-kits' && (
        <>
          {/* Search & View Toggle */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search brand kits..."
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 outline-none focus:border-[#6a1fbf]" />
            </div>
            <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                <Grid className="w-4 h-4 text-gray-400" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                <List className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Brand Kits Grid/List */}
          {filteredKits.length > 0 ? (
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-3'}>
              <AnimatePresence>
                {filteredKits.map(kit => {
                  const featureCounts = getFeatureCounts(kit);
                  const menuItems = [
                    { icon: Pencil, label: 'Edit', onClick: () => handleEdit(kit) },
                    { icon: Copy, label: 'Duplicate', onClick: () => handleDuplicate(kit) },
                    ...(kit.is_default
                      ? [{ icon: Star, label: 'Remove as Default', onClick: () => handleRemoveDefault(kit) }]
                      : [{ icon: Star, label: 'Set as Default', onClick: () => handleSetDefault(kit) }]
                    ),
                    ...(kit.is_global_default
                      ? [{ icon: Sparkles, label: 'Remove Global Default', onClick: () => handleRemoveGlobalDefault(kit) }]
                      : [{ icon: Sparkles, label: 'Set as Global Default', onClick: () => handleSetGlobalDefault(kit) }]
                    ),
                    { separator: true },
                    { icon: Trash2, label: 'Delete', onClick: () => handleDelete(kit), danger: true },
                  ];

                  return viewMode === 'grid' ? (
                    <motion.div key={kit.id} layout
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                      className="group bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                      <div className="h-16 relative overflow-hidden rounded-t-xl"
                        style={{ background: `linear-gradient(135deg, ${kit.colors?.primary || '#6a1fbf'}, ${kit.colors?.accent || '#00c853'})` }}>
                        {kit.logos?.main_logo_url && (
                          <img src={kit.logos.main_logo_url} alt="Logo" className="absolute inset-0 w-full h-full object-contain p-3 bg-black/20" />
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {kit.is_default && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
                            <h3 className="font-semibold text-white">{kit.name || 'Unnamed Kit'}</h3>
                          </div>
                          <DropdownMenu trigger={<MoreVertical className="w-4 h-4 text-gray-400" />} items={menuItems} />
                        </div>
                        {kit.description && <p className="text-sm text-gray-400 mb-3 line-clamp-2">{kit.description}</p>}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {featureCounts.map((f, i) => (
                            <span key={i} className="text-xs text-gray-400 bg-white/5 px-2 py-1 rounded">{f.emoji} {f.count} {f.label}</span>
                          ))}
                        </div>
                        <div className="flex gap-1 mb-4">
                          {Object.entries(kit.colors || {}).slice(0, 5).map(([key, color]) => (
                            <div key={key} className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: color }} title={key} />
                          ))}
                        </div>
                        <button onClick={() => handleEdit(kit)}
                          className="w-full flex items-center justify-center gap-2 py-2 bg-white/10 hover:bg-white/20 border border-white/10 text-white rounded-lg text-sm transition-colors">
                          <Pencil className="w-4 h-4" />Edit Brand Kit
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div key={kit.id} layout
                      initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                      className="group flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                      <div className="w-12 h-12 rounded-lg flex-shrink-0"
                        style={{ background: `linear-gradient(135deg, ${kit.colors?.primary || '#6a1fbf'}, ${kit.colors?.accent || '#00c853'})` }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {kit.is_default && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
                          <h3 className="font-semibold text-white truncate">{kit.name || 'Unnamed Kit'}</h3>
                        </div>
                        <p className="text-sm text-gray-400 truncate">{kit.description || 'No description'}</p>
                      </div>
                      <div className="hidden md:flex gap-1">
                        {Object.entries(kit.colors || {}).slice(0, 5).map(([key, color]) => (
                          <div key={key} className="w-5 h-5 rounded-full border border-white/20" style={{ backgroundColor: color }} title={key} />
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleEdit(kit)}
                          className="flex items-center gap-1 px-3 py-1.5 border border-white/10 text-white rounded-lg text-sm hover:bg-white/10 transition-colors">
                          <Pencil className="w-4 h-4" />Edit
                        </button>
                        <DropdownMenu trigger={<MoreVertical className="w-4 h-4 text-gray-400" />} items={menuItems} />
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          ) : (
            <div className="text-center py-16 rounded-xl border border-dashed border-white/20">
              <Palette className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No brand kits yet</h3>
              <p className="text-gray-400 mb-4">Create your first brand kit to customize your room's visual identity</p>
              <button onClick={handleCreate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all">
                <Plus className="w-4 h-4" />Create Brand Kit
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'templates' && (
        <BrandTemplatesSection onUseTemplate={handleUseTemplate} />
      )}

      {/* Editor Modal */}
      <BrandKitEditorModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingKit(null); }}
        onSave={handleSaveKit}
        brandKit={editingKit}
      />
    </div>
  );
}
